import React, { useState, useContext, useEffect } from "react";
import LineChart from "./components/LineChart";
import BestTimesKPIs from "./components/BestTimesKPIs";
import SharedDataContext from "../../../SharedDataContext";
import { useSmartQuery } from "../../../apiHooks/useSmartQuery";
import { getCallActivityBestTimes } from "../../../apis/callActivity";
import { useStoreCallActivity } from "../../../stores/useStoreCallActivity";
import { useQueryParams } from "../../../hooks/useQueryParams";
import useUpdateQueryParam from "../../../hooks/useUpdateQueryParam";

export default function BestTimesTab({ filters, hasAccess = true }) {
  const queryParams = useQueryParams();
  const updateQueryParam = useUpdateQueryParam();

  const [selectedCountry, setSelectedCountry] = useState(queryParams.country || "US");
  const [selectedTimezone, setSelectedTimezone] = useState(queryParams.timezone || "America/New_York");
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const { bestTimesData, setBestTimesData } = useStoreCallActivity();

  useEffect(() => {
    if (selectedCountry) {
      updateQueryParam("country", selectedCountry);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedTimezone) {
      updateQueryParam("timezone", selectedTimezone);
    }
  }, [selectedTimezone]);

  const getUserId = () => {
    if (filters.viewToggle === "Team") {
      return null;
    } else {
      return filters.userTeamSelector;
    }
  };

  const userId = getUserId();

  const buildQueryParams = () => {
    const params = new URLSearchParams();

    if (selectedCountry) {
      params.append("country", selectedCountry);
    }
    if (selectedTimezone) {
      params.append("timezone", selectedTimezone);
    }

    if (filters.dateRange) {
      if (filters.dateRange === "custom") {
        params.append("time_range", "custom");
        if (filters.customStartDate) {
          params.append("start_date", filters.customStartDate);
        }
        if (filters.customEndDate) {
          params.append("end_date", filters.customEndDate);
        }
      } else {
        const dateRangeMap = {
          "7 days": "7d",
          "30 days": "30d",
          "60 days": "60d",
          "90 days": "90d",
          "180 days": "180d",
          "1 year": "1y",
        };
        params.append("time_range", dateRangeMap[filters.dateRange] || "30d");
      }
    }

    return params.toString();
  };

  const apiQueryParams = buildQueryParams();

  const handleCountryChange = (countryCode) => {
    if (!countryCode || countryCode === selectedCountry) {
      return;
    }
    setSelectedCountry(countryCode);
    setSelectedTimezone(null);
  };

  const { isLoading, error } = useSmartQuery(
    ["call_activity_best_times", activeWorkspace?.id, userId, apiQueryParams],
    () =>
      getCallActivityBestTimes({
        userId,
        query: apiQueryParams,
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
            "Content-Type": "application/json",
          },
        },
      }),
    {
      condition: !!activeWorkspace?.id && hasAccess && !!selectedTimezone,
      zustandSetter: setBestTimesData,
    }
  );

  // Use real data if available, fallback to loading state
  const data = bestTimesData?.data || null;

  return (
    <div className="space-y-6">
      <div>
        <BestTimesKPIs
          data={data}
          selectedCountry={selectedCountry}
          selectedTimezone={selectedTimezone}
          onCountryChange={handleCountryChange}
          onTimezoneChange={setSelectedTimezone}
          isLoading={isLoading}
          error={error}
        />
      </div>
      <LineChart data={data?.graph} isLoading={isLoading} error={error} selectedTimezone={selectedTimezone} />
    </div>
  );
}
